// src/components/PublicFooter.jsx - FIXED VERSION
import React from 'react';
import { Link } from 'react-router-dom';
import { Facebook, Twitter, Instagram, Linkedin, MapPin, Mail } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const FooterSection = ({ title, links }) => (
    <div>
        <h3 className="text-lg font-semibold text-gray-100 mb-4">{title}</h3>
        <ul className="space-y-3">
            {links.map((link, index) => (
                <li key={index}>
                    {link.to.startsWith('http') ? (
                        <a href={link.to} target="_blank" rel="noopener noreferrer" className="text-gray-300 hover:text-white transition-colors text-sm">
                            {link.name}
                        </a>
                    ) : (
                        <Link to={link.to} className="text-gray-300 hover:text-white transition-colors text-sm">
                            {link.name}
                        </Link>
                    )}
                </li>
            ))}
        </ul>
    </div>
);

const PublicFooter = () => {
    const { t } = useTranslation();

    return (
        <footer className="bg-gray-800 border-t border-gray-700 mt-12">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-10">
                <div className="grid grid-cols-2 md:grid-cols-5 gap-8">
                    {/* Column 1: Logo & Social */}
                    <div className="col-span-2 md:col-span-1 space-y-4">
                        <Link to="/" className="flex items-center">
                            <img src="/gigship-logo-footer.png" alt="GigShip Logo" className="h-8 w-auto" />
                        </Link>
                        <p className="text-gray-400 text-sm">
                            {t('gigship_description')}
                        </p>
                        <div className="flex space-x-4">
                            <a
                                href="https://facebook.com/gigship"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-gray-400 hover:text-white transition-colors"
                                aria-label="GigShip on Facebook"
                            >
                                <Facebook className="w-5 h-5" />
                            </a>
                            <a
                                href="https://twitter.com/gigship"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-gray-400 hover:text-white transition-colors"
                                aria-label="GigShip on Twitter"
                            >
                                <Twitter className="w-5 h-5" />
                            </a>
                            <a
                                href="https://instagram.com/gigship"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-gray-400 hover:text-white transition-colors"
                                aria-label="GigShip on Instagram"
                            >
                                <Instagram className="w-5 h-5" />
                            </a>
                            <a
                                href="https://linkedin.com/company/gigship"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-gray-400 hover:text-white transition-colors"
                                aria-label="GigShip on LinkedIn"
                            >
                                <Linkedin className="w-5 h-5" />
                            </a>
                        </div>
                    </div>

                    {/* Column 2: GigShip */}
                    <FooterSection
                        title="GigShip"
                        links={[
                            { name: t('about_us'), to: '/about' },
                            { name: t('how_it_works'), to: '/how-it-works' },
                            { name: t('frequently_asked_questions'), to: '/faq' },
                            { name: t('documentation'), to: '/documentation' },
                            { name: t('help_support'), to: '/support' },
                        ]}
                    />

                    {/* Column 3: For Shippers */}
                    <FooterSection
                        title={t('for_shippers')}
                        links={[
                            { name: t('become_shipper'), to: '/register?type=shipper' },
                            { name: t('request_service'), to: '/dashboard/requests/create' },
                            { name: t('see_latest_gigs'), to: '/gigs' },
                            { name: t('shipper_levels'), to: '/user-levels' },
                            { name: t('refer_friend'), to: '/referral-program' },
                        ]}
                    />

                    {/* Column 4: For Giggers */}
                    <FooterSection
                        title={t('for_giggers')}
                        links={[
                            { name: t('become_gigger'), to: '/register?type=gigger' },
                            { name: t('post_gig'), to: '/dashboard/gigs/create' },
                            { name: t('see_latest_requests'), to: '/requests' },
                            { name: t('gigger_levels'), to: '/user-levels' },
                            { name: t('refer_friend'), to: '/referral-program' },
                        ]}
                    />

                    {/* Column 5: Legal */}
                    <FooterSection
                        title={t('legal_privacy')}
                        links={[
                            { name: t('terms'), to: '/terms' },
                            { name: t('privacy'), to: '/privacy' },
                            { name: t('verifications'), to: '/verifications' },
                            { name: t('trust_safety'), to: '/trust-safety' },
                            { name: t('cookie_policy'), to: '/cookies-policy' },
                        ]}
                    />
                </div>

                <div className="mt-8 pt-6 border-t border-gray-700">
                    <div className="flex flex-col md:flex-row justify-between items-center">
                        <p className="text-sm text-gray-400 text-center md:text-left">
                            &copy; {new Date().getFullYear()} GigShip. {t('all_rights_reserved')}.
                            <span className="md:ml-4 block md:inline-block">
                                {t('powered_by')} <Link to="https://cosbyte.com" className="text-gray-300 hover:text-white transition-colors text-sm" target='_blank' title='Visit Cosbyte Website'>
                                    Cosbyte Technologies Ltd.
                                </Link>
                            </span>
                        </p>
                        <div className="flex items-center space-x-4 mt-4 md:mt-0">
                            <div className="flex items-center text-sm text-gray-400">
                                <MapPin className="w-4 h-4 mr-1" /> Accra, Ghana
                            </div>
                            <a href="mailto:support@gigship.com" className="flex items-center text-sm text-gray-400 hover:text-white">
                                <Mail className="w-4 h-4 mr-1" /> support@gigship.com
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        </footer>
    );
};

export default PublicFooter;